import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { PenSquare, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

const KINDS = [
  { value: "creator_reply", label: "Creator reply" },
  { value: "member_reply", label: "Member reply" },
  { value: "paid_send", label: "Paid send" },
  { value: "reengagement", label: "Re-engagement" },
];

export function ComposeDraftDialog({
  open,
  onOpenChange,
  onComposed,
  defaultCreatorId,
  defaultMemberId,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onComposed?: (draftId: string) => void;
  defaultCreatorId?: string;
  defaultMemberId?: string;
}) {
  const creatorsQ = useQuery({
    queryKey: ["compose", "creators"],
    queryFn: () => api.creatorsList({ limit: 100 }),
    enabled: open,
    staleTime: 60_000,
  });

  const [creatorId, setCreatorId] = React.useState(defaultCreatorId ?? "");
  const [memberId, setMemberId] = React.useState(defaultMemberId ?? "");
  const [kind, setKind] = React.useState("creator_reply");
  const [chunks, setChunks] = React.useState<string[]>([""]);
  const [note, setNote] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      setCreatorId(defaultCreatorId ?? "");
      setMemberId(defaultMemberId ?? "");
      setKind("creator_reply");
      setChunks([""]);
      setNote("");
      setError(null);
    }
  }, [open, defaultCreatorId, defaultMemberId]);

  const cleaned = chunks.map((c) => c.trim()).filter(Boolean);
  const valid = creatorId && memberId && cleaned.length > 0;

  const onSubmit = async () => {
    if (!valid) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.composeDraft({
        creator_id: creatorId,
        member_id: memberId.trim(),
        chunks: cleaned,
        kind,
        note: note.trim() || undefined,
      });
      onComposed?.(res.draft_id);
      onOpenChange(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenSquare className="h-4 w-4" /> Compose a draft
          </DialogTitle>
          <DialogDescription>
            Queues a manually-written message for your own approval. It will
            appear in /drafts and goes out only when you approve it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="text-2xs uppercase text-muted-foreground">Creator</label>
            <select
              value={creatorId}
              onChange={(e) => setCreatorId(e.target.value)}
              className="mt-1 h-8 w-full rounded border bg-background px-2 text-sm"
            >
              <option value="">Choose a creator…</option>
              {creatorsQ.data?.items.map((c) => (
                <option key={c.creator_id} value={c.creator_id}>
                  {c.display_name || c.handle} (@{c.handle})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-2xs uppercase text-muted-foreground">
              Member (phone or iMessage GUID)
            </label>
            <Input
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
              placeholder="+1555… or any;-;+1555…"
              className="mt-1 text-sm"
            />
          </div>

          <div>
            <label className="text-2xs uppercase text-muted-foreground">Kind</label>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="mt-1 h-8 w-full rounded border bg-background px-2 text-sm"
            >
              {KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-2xs uppercase text-muted-foreground">
              Message ({chunks.length} chunk{chunks.length === 1 ? "" : "s"})
            </label>
            <div className="mt-1 space-y-2">
              {chunks.map((c, i) => (
                <Textarea
                  key={i}
                  value={c}
                  rows={2}
                  placeholder={i === 0 ? "Type the message…" : `Chunk ${i + 1}…`}
                  onChange={(e) => {
                    const next = [...chunks];
                    next[i] = e.target.value;
                    setChunks(next);
                  }}
                />
              ))}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setChunks([...chunks, ""])}>
                  + chunk
                </Button>
                {chunks.length > 1 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setChunks(chunks.slice(0, -1))}
                  >
                    − chunk
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="text-2xs uppercase text-muted-foreground">
              Note (optional, shown to approver)
            </label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="why this matters…"
              className="mt-1 text-sm"
            />
          </div>

          {error && (
            <div className="rounded-md bg-rose-50 px-3 py-2 text-xs text-rose-700">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 border-t pt-3">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={onSubmit} disabled={!valid || submitting}>
              <Send className="mr-1 h-3 w-3" />
              {submitting ? "Queuing…" : "Queue for approval"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
